/**
 * carta-live-runtime.js — la CARTA EN VIVO del bloque `menu` conectado al ERP (ola 3 · T1b).
 *
 * Lo publicado lleva HORNEADA la carta del día en que se publicó (para buscadores y para quien no
 * ejecuta JS). Este runtime la sustituye por la de AHORA: lee los parámetros de
 * `.menu-categories[data-carta-live]` (los escribe `blocks/menu.js`), pide `GET /site_carta/?r=&l=`
 * —el MISMO endpoint público que la carta del QR, con su caché de 60 s y su puerta— y repinta con el
 * MISMO pintor que el editor (`utils/carta.js`). Así, cambiar un precio o un plato en el ERP se ve en
 * la web sin volver a publicar.
 *
 * Qué hace con cada respuesta:
 *   · `ok`                        → repinta la carta COMPLETA (el vivo no hornea: no hay presupuesto).
 *   · `module_off` / `unavailable` → vacía la carta y ESCONDE la sección. `module_off` = el dueño apagó
 *     «Carta pública» (lo apagado no se enseña, tampoco lo horneado); `unavailable` = no se pueden
 *     garantizar los alérgenos (regla H2: una carta sin chips le diría a un alérgico que el plato no
 *     lleva nada — mejor no enseñar ninguna).
 *   · red, tiempo agotado, 429 o cualquier otra cosa → se queda lo horneado, sin tocar nada.
 *
 * Solo corre donde `basePublica() === ''`: el sitio publicado y la SPA pública en vivo. En el editor
 * (y en su «Ver sitio») manda la FOTO guardada, que es lo que se va a publicar.
 *
 * Nunca lanza, no usa `window.icons` ni nada del editor, y la marca de ya pintado va en el DOM
 * (`data-cl-ready`, molde `reserva-live-runtime.js`): tras un repintado de la SPA, el elemento es
 * nuevo y se vuelve a pedir. Lo arranca `core/block-actions.js` (al cargar y tras cada render).
 */
import { htmlCarta, fotoDesdeCarta, presentacionDeVivo, opcionesDePresentacion } from '../utils/carta.js';
import { basePublica } from '../utils/base-publica.js';

export const CARTA_ENDPOINT = '/site_carta/';
const TIEMPO_MAX_MS = 6000;

/** GET que NUNCA lanza: el JSON si la respuesta es 2xx; `null` ante red, tiempo agotado, 4xx/5xx. */
async function pedir(url, ms) {
    const ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    const to = setTimeout(() => { if (ctrl) ctrl.abort(); }, ms);
    try {
        const res = await fetch(url, { signal: ctrl ? ctrl.signal : undefined, headers: { Accept: 'application/json' } });
        if (!res || !res.ok) return null;
        return await res.json();
    } catch (_) {
        return null;
    } finally {
        clearTimeout(to);
    }
}

/**
 * Los parámetros de `data-carta-live`, validados, o `null` si no hay a quién preguntar (entonces NO
 * se pide nada: un `r` que no es un id no llega al servidor).
 *   r: id del local (`^\d{1,10}$`) · l: idioma (`^[a-z]{2}$`, si no 'es') · p: 1 = ocultar precios
 *   s: 'todas' | [ids de sección] · x: prefijo de los ids del sprite (`^[a-z0-9-]{1,20}$`, si no 'mc')
 *   y la PRESENTACIÓN (a, v, n, m, o, d, t: `utils/carta.js::vivoDePresentacion`) → `pres` + `textos`
 */
export function parametrosCarta(crudo) {
    let d = null;
    try { d = JSON.parse(crudo || ''); } catch (_) { return null; }
    if (!d || typeof d !== 'object' || Array.isArray(d)) return null;
    const r = typeof d.r === 'number' ? String(d.r) : d.r;
    if (typeof r !== 'string' || !/^\d{1,10}$/.test(r)) return null;
    const s = Array.isArray(d.s)
        ? d.s.map((v) => String(v)).filter((v) => /^\d{1,10}$/.test(v))
        : 'todas';
    const pv = presentacionDeVivo(d);
    return {
        r: r,
        l: (typeof d.l === 'string' && /^[a-z]{2}$/.test(d.l)) ? d.l : 'es',
        p: d.p === 1 ? 1 : 0,
        s: s,
        x: (typeof d.x === 'string' && /^[a-z0-9-]{1,20}$/.test(d.x)) ? d.x : 'mc',
        pres: pv.pres,
        textos: pv.textos,
    };
}

async function refrescarCarta(el, prm) {
    const url = basePublica() + CARTA_ENDPOINT + '?r=' + encodeURIComponent(prm.r) + '&l=' + encodeURIComponent(prm.l);
    const data = await pedir(url, TIEMPO_MAX_MS);
    if (!data || typeof data !== 'object') return;          // red, 429, 5xx…: queda lo horneado
    try {
        if (data.ok === true && data.carta && typeof data.carta === 'object') {
            const f = fotoDesdeCarta(data.carta,
                { secciones: prm.s, ocultos: prm.pres.ocultos, destacados: prm.pres.destacados });
            el.innerHTML = htmlCarta(f.categories, f.alergenos, Object.assign(
                { ocultarPrecios: prm.p === 1, prefijo: prm.x, compacta: false },
                opcionesDePresentacion(prm.pres, prm.textos)));
            // Visibles YA: el rescate `.in-view` de los stagger (site.css, sin data-anim) solo corre
            // al cargar la página, y lo repintado nace después.
            const items = el.querySelectorAll('.stagger-item');
            for (let i = 0; i < items.length; i++) items[i].classList.add('in-view');
            return;
        }
        if (data.ok === false && (data.error === 'module_off' || data.error === 'unavailable')) {
            el.innerHTML = '';
            const seccion = el.closest ? el.closest('section') : null;
            if (seccion) seccion.style.display = 'none';
        }
    } catch (_) {
        /* un repintado fallido deja lo que hubiera: nunca rompe la página */
    }
}

/** Pinta EN VIVO las cartas que aún no lo estén. Idempotente (marca `data-cl-ready` en el DOM). */
export function hydrateCartaLive() {
    if (typeof document === 'undefined') return;
    if (basePublica() !== '') return;                        // editor y su «Ver sitio»: manda la foto
    const nodos = document.querySelectorAll('.menu-categories[data-carta-live]:not([data-cl-ready])');
    for (let i = 0; i < nodos.length; i++) {
        const el = nodos[i];
        el.setAttribute('data-cl-ready', '1');
        const prm = parametrosCarta(el.getAttribute('data-carta-live'));
        if (prm) refrescarCarta(el, prm);
    }
}
